import { ClickAwayListener, Popper } from "@mui/material";
import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import Button from "../Button/Button";
import { combineClasses } from "@/lib/utils";
import styles from "./DateRangeCalendar.module.scss";

type CalendarMode = "single" | "range";

interface BaseCalendarProps {
  className?: string | undefined;
  min?: string | undefined;
  max?: string | undefined;
  onClose?: (() => void) | undefined;
}

interface SingleDateCalendarProps extends BaseCalendarProps {
  mode: "single";
  value?: string | undefined;
  onChange: (date: string) => void;
  onBlur?: (() => void) | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  name?: string | undefined;
  showClearButton?: boolean | undefined;
}

interface RangeDateCalendarProps extends BaseCalendarProps {
  mode?: "range" | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  onApply: (startDate: string, endDate: string) => void;
}

type DateRangeCalendarProps = SingleDateCalendarProps | RangeDateCalendarProps;

const WEEK_DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const padValue = (value: number) => String(value).padStart(2, "0");

const formatDateValue = (date: Date) =>
  `${date.getFullYear()}-${padValue(date.getMonth() + 1)}-${padValue(
    date.getDate(),
  )}`;

const parseDateValue = (value?: string) => {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return null;

  const parsedDate = new Date(year, month - 1, day);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDisplayDate = (value?: string, placeholder = "dd/mm/yyyy") => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) return placeholder;

  return `${padValue(parsedDate.getDate())}/${padValue(
    parsedDate.getMonth() + 1,
  )}/${parsedDate.getFullYear()}`;
};

const startOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const isDateDisabled = (date: Date, min?: string, max?: string) => {
  const dateKey = formatDateValue(date);

  if (min && dateKey < min) return true;
  if (max && dateKey > max) return true;

  return false;
};

const canNavigateToPreviousMonth = (displayMonth: Date, min?: string) => {
  if (!min) return true;

  const previousMonthLastDay = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth(),
    0,
  );

  return formatDateValue(previousMonthLastDay) >= min;
};

const canNavigateToNextMonth = (displayMonth: Date, max?: string) => {
  if (!max) return true;

  const nextMonthFirstDay = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() + 1,
    1,
  );

  return formatDateValue(nextMonthFirstDay) <= max;
};


const CalendarIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="18"
    viewBox="0 0 24 24"
    width="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 2V5M16 2V5M3 9H21M5 4H19C20.1046 4 21 4.89543 21 6V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V6C3 4.89543 3.89543 4 5 4Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    viewBox="0 0 24 24"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="16"
    viewBox="0 0 24 24"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18L15 12L9 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
);

interface CalendarPanelProps extends BaseCalendarProps {
  value?: string | undefined;
  onChange: (date: string) => void;
}

const CalendarPanel = ({
  value,
  min,
  max,
  onChange,
  onClose,
  className,
}: CalendarPanelProps) => {
  const today = useMemo(() => new Date(), []);
  const selectedDate = parseDateValue(value);
  const [displayMonth, setDisplayMonth] = useState<Date>(
    startOfMonth(selectedDate ?? parseDateValue(max) ?? today),
  );
  const [showYearPicker, setShowYearPicker] = useState(false);
  const yearPickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const parsed = parseDateValue(value);
    if (parsed) {
      setDisplayMonth(startOfMonth(parsed));
    }
  }, [value]);

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = startOfMonth(displayMonth);
    const gridStart = new Date(firstDayOfMonth);
    gridStart.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const currentDate = new Date(gridStart);
      currentDate.setDate(gridStart.getDate() + index);

      return {
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === displayMonth.getMonth(),
        disabled: isDateDisabled(currentDate, min, max),
      };
    });
  }, [displayMonth, max, min]);

  const handleSelectDate = (date: Date) => {
    if (isDateDisabled(date, min, max)) return;

    onChange(formatDateValue(date));
    onClose?.();
  };

  const handleClearClick = () => {
    onChange("");
    onClose?.();
  };

  const canGoToPreviousMonth = canNavigateToPreviousMonth(displayMonth, min);
  const canGoToNextMonth = canNavigateToNextMonth(displayMonth, max);

  const currentYear = displayMonth.getFullYear();
  const minYear = min ? Number(min.split("-")[0]) : currentYear - 50;
  const maxYear = max ? Number(max.split("-")[0]) : currentYear + 50;
  // const yearRange = Array.from(
  //   { length: maxYear - minYear + 1 },
  //   (_, i) => minYear + i,
  // );

  const YEARS_PER_PAGE = 12;

  const initialYearPage = Math.floor((currentYear - minYear) / YEARS_PER_PAGE);

  const [yearPage, setYearPage] = useState(initialYearPage);

  const startYear = minYear + yearPage * YEARS_PER_PAGE;

  const visibleYears = Array.from(
    { length: YEARS_PER_PAGE },
    (_, i) => startYear + i,
  ).filter((year) => year <= maxYear);

  const handleYearSelect = (year: number) => {
    setDisplayMonth(new Date(year, displayMonth.getMonth(), 1));
    setShowYearPicker(false);
  };

  return (
    <div className={combineClasses(styles.calendarContainer, className)}>
      <div className={styles.header}>
        <button
          aria-label="Previous month"
          className={styles.navButton}
          disabled={!canGoToPreviousMonth}
          onClick={() =>
            canGoToPreviousMonth &&
            setDisplayMonth(
              new Date(
                displayMonth.getFullYear(),
                displayMonth.getMonth() - 1,
                1,
              ),
            )
          }
          type="button"
        >
          <ChevronLeftIcon />
        </button>

        <div className={styles.monthLabel}>
          <button
            className={styles.yearButton}
            onClick={() => setShowYearPicker(!showYearPicker)}
            type="button"
          >
            {displayMonth.getFullYear()}
          </button>
          <span className={styles.monthName}>
            {new Intl.DateTimeFormat("en-US", { month: "long" }).format(
              displayMonth,
            )}
          </span>
        </div>

        <button
          aria-label="Next month"
          className={styles.navButton}
          disabled={!canGoToNextMonth}
          onClick={() =>
            canGoToNextMonth &&
            setDisplayMonth(
              new Date(
                displayMonth.getFullYear(),
                displayMonth.getMonth() + 1,
                1,
              ),
            )
          }
          type="button"
        >
          <ChevronRightIcon />
        </button>
      </div>

      {!showYearPicker && (
        <>
          <div className={styles.weekdays}>
            {WEEK_DAYS.map((day) => (
              <span className={styles.weekday} key={day}>
                {day}
              </span>
            ))}
          </div>

          <div className={styles.daysGrid}>
            {calendarDays.map(({ date, isCurrentMonth, disabled }) => {
              const dateKey = formatDateValue(date);
              const isToday = dateKey === formatDateValue(today);
              const isSelected = value === dateKey;

              return (
                <button
                  aria-pressed={isSelected}
                  className={combineClasses(
                    styles.dayButton,
                    !isCurrentMonth && styles.dayOutside,
                    isToday && styles.dayToday,
                    isSelected && styles.daySelected,
                    disabled && styles.dayDisabled,
                  )}
                  disabled={disabled}
                  key={dateKey}
                  onClick={() => handleSelectDate(date)}
                  type="button"
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </>
      )}

      {showYearPicker && (
        <div
          ref={yearPickerRef}
          className={styles.yearPickerContent}
          role="dialog"
          aria-label="Select year"
        >
          <div className={styles.yearPickerHeader}>
            <button
              className={styles.yearNavButton}
              disabled={yearPage === 0}
              onClick={() => setYearPage((prev) => Math.max(prev - 1, 0))}
              type="button"
            >
              ‹
            </button>
            <span className={styles.yearRange}>
              {startYear} – {Math.min(startYear + YEARS_PER_PAGE - 1, maxYear)}
            </span>
            <button
              className={styles.yearNavButton}
              disabled={startYear + YEARS_PER_PAGE > maxYear}
              onClick={() => {
                if (startYear + YEARS_PER_PAGE <= maxYear) {
                  setYearPage((prev) => prev + 1);
                }
              }}
              type="button"
            >
              ›
            </button>
          </div>
          <div className={styles.yearGrid}>
            {visibleYears.map((year) => (
              <button
                key={year}
                className={combineClasses(
                  styles.yearOption,
                  year === currentYear && styles.yearOptionSelected,
                )}
                onClick={() => handleYearSelect(year)}
                type="button"
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <button
          className={styles.footerButton}
          onClick={handleClearClick}
          type="button"
        >
          Clear
        </button>  
      </div>
    </div>
  );
};

const SingleDateCalendar = ({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  error,
  name,
  className,
  min,
  max,
  showClearButton,
}: Omit<SingleDateCalendarProps, "mode">) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const closePicker = () => {
    setIsOpen(false);
    onBlur?.();
  };

  const togglePicker = () => {
    if (disabled) return;

    setIsOpen((previous) => !previous);
  };

  const handleClearSelection = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onChange("");
    onBlur?.();
  };

  return (
    <div className={combineClasses(styles.pickerWrapper, className)}>
      <button
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={combineClasses(
          styles.inputTrigger,
          isOpen && styles.inputTriggerOpen,
          error && styles.inputTriggerError,
        )}
        disabled={disabled}
        id={name}
        onClick={togglePicker}
        type="button"
      >
        <span
          className={combineClasses(
            styles.valueText,
            !value && styles.placeholderText,
          )}
        >
          {formatDisplayDate(value, placeholder || "dd/mm/yyyy")}
        </span>
        {showClearButton && value ? (
          <button
            aria-label="Clear date"
            className={styles.clearButton}
            onClick={handleClearSelection}
            type="button"
          >
            ×
          </button>
        ) : null}
        <span className={styles.calendarIcon}>
          <CalendarIcon />
        </span>
      </button>

      <Popper
        anchorEl={triggerRef.current}
        className={styles.popper}
        open={isOpen}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={closePicker}>
          <div className={styles.popup} role="dialog" aria-label="Choose date">
            <CalendarPanel
              max={max}
              min={min}
              onChange={onChange}
              onClose={closePicker}
              value={value}
            />
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

const RangeDateCalendar = ({
  startDate: initialStartDate,
  endDate: initialEndDate,
  onApply,
  onClose,
  className,
}: Omit<RangeDateCalendarProps, "mode">) => {
  const today = useMemo(() => new Date().toISOString().split("T")[0] ?? "", []);
  const [startDate, setStartDate] = useState<string>(initialStartDate || today);
  const [endDate, setEndDate] = useState<string>(initialEndDate || today);

  useEffect(() => {
    setStartDate(initialStartDate || today);
  }, [initialStartDate, today]);

  useEffect(() => {
    setEndDate(initialEndDate || today);
  }, [initialEndDate, today]);

  const handleApply = () => {
    if (!startDate || !endDate) return;

    onApply(startDate, endDate);
    onClose?.();
  };

  return (
    <div className={combineClasses(styles.calendarContainer, className)}>
      <div className={styles.rangeInputs}>
        <div className={styles.rangeField}>
          <label htmlFor="range-start-date">Start Date</label>
          <input
            id="range-start-date"
            onChange={(event) => setStartDate(event.target.value)}
            type="date"
            value={startDate}
          />
        </div>
        <div className={styles.rangeField}>
          <label htmlFor="range-end-date">End Date</label>
          <input
            id="range-end-date"
            onChange={(event) => setEndDate(event.target.value)}
            type="date"
            value={endDate}
          />
        </div>
      </div>
      <div className={styles.rangeActions}>
        <Button outlined onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleApply}>Apply</Button>
      </div>
    </div>
  );
};

const DateRangeCalendar = (props: DateRangeCalendarProps) => {
  const mode: CalendarMode = props.mode || "range";

  if (mode === "single") {
    const { mode: _mode, ...singleProps } = props as SingleDateCalendarProps;
    return <SingleDateCalendar {...singleProps} />;
  }

  const { mode: _mode, ...rangeProps } = props as RangeDateCalendarProps;
  return <RangeDateCalendar {...rangeProps} />;
};

export default DateRangeCalendar;
